/**
 * Profile-photo prep: center-crop to a square and downscale, so a 6 MB phone photo
 * doesn't bounce off the 2 MB upload limit and every avatar is the same shape.
 *
 * Best-effort by design — if the browser can't decode or draw it (no
 * createImageBitmap, a format it can't read, no canvas), the ORIGINAL file is
 * returned and the caller's size/type checks decide. It never throws.
 */
export async function prepareAvatarImage(file: File, size = 512): Promise<File> {
  try {
    if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;
    // 'from-image' (the default) applies the EXIF rotation, so portraits aren't sideways.
    const bitmap = await createImageBitmap(file);
    const side = Math.min(bitmap.width, bitmap.height);
    const target = Math.min(size, side);
    const canvas = document.createElement('canvas');
    canvas.width = target;
    canvas.height = target;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    // JPEG has no alpha — flatten transparent PNGs onto white rather than black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, target, target);
    ctx.drawImage(
      bitmap,
      (bitmap.width - side) / 2,
      (bitmap.height - side) / 2,
      side,
      side,
      0,
      0,
      target,
      target
    );
    bitmap.close?.();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.9)
    );
    return blob ? new File([blob], 'avatar.jpg', { type: 'image/jpeg' }) : file;
  } catch {
    return file;
  }
}
